import { createIsomorphicFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { isLocale, parseLocale, type Locale } from 'shared/locales'

/**
 * Resolve the request's locale on the SERVER so SSR renders UI strings in the same
 * locale the client will hydrate with. Mirrors app-store's client precedence
 * (`?lang` → `language` cookie → the browser's language) but reads it from the
 * request instead of `window`/`navigator`: `?lang` search param → `language`
 * cookie → the `Accept-Language` header (the server-side equivalent of
 * `navigator.language`).
 *
 * Before this, SSR always activated the `es-ES` default (the server store reads
 * cookies only, and there's no `language` cookie for a fresh visitor), so an
 * English browser (`navigator.language: en-US`) hydrated against Spanish HTML —
 * a locale hydration mismatch (React #418) that left stale server attributes like
 * the user-avatar `aria-label` in place.
 *
 * Returns `null` on the client: there the store already derives the locale from
 * `navigator.language` at module load, and the root loader doesn't re-run on the
 * initial (dehydrated) hydration anyway.
 */
export const resolveServerLocale = createIsomorphicFn()
  .server((): Locale | null => {
    const req = getRequest()
    const url = new URL(req.url)

    const langParam = url.searchParams.get('lang')
    if (isLocale(langParam)) return langParam

    const cookieHeader = req.headers.get('cookie') ?? ''
    const cookieMatch = /(?:^|;\s*)language=([^;]+)/.exec(cookieHeader)
    const cookieLang = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]) : undefined
    if (isLocale(cookieLang)) return cookieLang

    return parseLocale({ 'accept-language': req.headers.get('accept-language') ?? undefined })
  })
  .client((): Locale | null => null)
